// VpnForm.tsx
import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  Stack,
  CircularProgress,
  Alert,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import { Formik, Form, Field, type FormikProps, type FieldProps } from 'formik';
import * as Yup from 'yup';
import { useTranslation } from 'react-i18next';
import { WireguardConf } from './vpn';
import { useCustomer } from '../../context/CustomerContext';

export interface VpnFormProps {
  wc: WireguardConf | null;
  onSave: (values: WireguardConf) => Promise<void> | void;  
  onCancel: () => void;
}

export const SelectByBool = (v: boolean, t: string, f: string, none: string) => {
  if (v === true) return t;
  if (v === false) return f;
  return none;
}

const VpnForm: React.FC<VpnFormProps> = ({
  wc,
  onSave,
  onCancel,
}) => {
  const { t } = useTranslation();
  const title = wc ? t('VpnForm.Edit_Configuration') : t('VpnForm.Add_New_Configuration');
  const { customerData, isLoading: isCustomerLoading } = useCustomer();
  const [saveError, setSaveError] = useState<string | null>(null);

  const initialValues: WireguardConf = wc || {
    customer_id: customerData?.customer || -1,
    id: null,
    name: '',
    conf: '{}',
    enabled: false,
    comment: '',
    row_hash: '',
  };

  const validationSchema = Yup.object({
    name: Yup.string().required(('Name_is_required')),
    conf: Yup.string().required(('Configuration_is_required')),
  });

  if (customerData?.customer === -1) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 3 }}>
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>{('Loading_form_data')}</Typography>
      </Box>
    );
  }

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={async (values, { setSubmitting }) => {
        // Очищаємо попередню помилку
        setSaveError(null); 
        try {
          await Promise.resolve(onSave(values)); 
        } catch (error) {
          console.error("Помилка збереження WireguardConf:", error);
          const errorMessage = error instanceof Error ? error.message : t('VpnForm.Unknown_Save_Error');
          setSaveError(t('VpnForm.Save_Failed', { error: errorMessage }));
          setSubmitting(false); 
        }
      }}      
      enableReinitialize
    >
      {(formikProps: FormikProps<WireguardConf>) => {
        const { values, setFieldValue } = formikProps;

        return (
          <Form>
            <Typography variant="h6" gutterBottom>
              {title}
            </Typography>

            {saveError && (
              <Alert 
                severity="error" 
                sx={{ mb: 2 }}
                onClose={() => setSaveError(null)} // Дозволяємо закрити повідомлення
              >
                {saveError}
              </Alert>
            )}
            
            <Stack spacing={2} sx={{ mt: 2 }}>

              <Field name="name">
                {({ field, meta }: { field: any; meta: any }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label={t('VpnForm.Name_Label')}
                    error={meta.touched && !!meta.error}
                    helperText={(meta.touched && meta.error) || t('VpnForm.Name_Helper_Text')}
                  />
                )}
              </Field>

              <Field name="conf">
                {({ field, meta }: { field: any; meta: any }) => (
                  <TextField
                    {...field}
                    fullWidth
                    multiline
                    rows={3}
                    label={t('VpnList.conf_Label')}
                    error={meta.touched && !!meta.error}
                    helperText={(meta.touched && meta.error) || t('VpnList.conf_Label')}
                    sx={{
                      '& textarea': {
                        resize: 'vertical',
                        minHeight: '80px',   // Минимальная высота, чтобы поле не схлопывалось
                      },
                    }}                    
                  />
                )}
              </Field>
              
              <Field name="enabled" type="checkbox">
                {({ field }: { field: any }) => (
                  <FormControlLabel
                    control={<Checkbox {...field} checked={field.value} />}
                    label={t('OriginForm.Enabled_Label')}
                  />
                )}
              </Field>

              <Field name="comment">
                {({ field, meta }: { field: any; meta: any }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label={t('VpnList.comment_Label')}
                    error={meta.touched && !!meta.error}
                    helperText={(meta.touched && meta.error) || t('VpnList.comment_Label')}
                  />
                )}
              </Field>

            </Stack>

            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Button onClick={onCancel} sx={{ mr: 1 }} disabled={formikProps.isSubmitting} variant='outlined'>
                  {t('OriginForm.Cancel')}
                </Button>
                <Button type="submit" variant="contained" disabled={formikProps.isSubmitting || !formikProps.isValid || !formikProps.dirty}>
                  {formikProps.isSubmitting ? t('VpnForm.Saving') : t('VpnForm.Save')}
                </Button>
              </Box>
            </Box>

          </Form>
        );
      }}
    </Formik>
  );
};

export default VpnForm;